import { authService, authSignupRequestSchema, TAuthSignupRequestPayload } from "@/api/auth";
import { LEGAL_DOCUMENT_TYPES, legalService, TLegalAcceptanceInput, TLegalDocument } from "@/api/legal";
import { userService } from "@/api/user/user.service";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { DefaultValues, useForm } from "react-hook-form";

import { useToast } from "@/components/ui";
import { useStore } from "@/store";
import { handleFormError } from "@/utils/formErrorUtils";
import { getJwtExpiration } from "@/utils/jwt";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useRouter } from "@tanstack/react-router";

export type TUseSignupFormProps = {
  defaultValues?: DefaultValues<TAuthSignupRequestPayload>;
  /** Investor invitation token (006/RD-4) — forwarded to the register call. */
  inviteToken?: string;
};

export const useSignupForm = ({ defaultValues, inviteToken }: TUseSignupFormProps = {}) => {
  const form = useForm<TAuthSignupRequestPayload>({
    resolver: zodResolver(authSignupRequestSchema),
    defaultValues: {
      acceptTerms: false,
      ...defaultValues,
    },
  });

  const { toast } = useToast();
  const navigate = useNavigate();
  const router = useRouter();
  const updateAuthStore = useStore.use.updateAuthStore();
  const [legalDocuments, setLegalDocuments] = useState<{
    terms?: TLegalDocument;
    privacy?: TLegalDocument;
  }>({});
  const [legalDocumentsLoading, setLegalDocumentsLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLegalDocumentsLoading(true);
      const [termsResult, privacyResult] = await Promise.all([
        legalService.getActiveDocument(LEGAL_DOCUMENT_TYPES.termsOfService),
        legalService.getActiveDocument(LEGAL_DOCUMENT_TYPES.privacyPolicy),
      ]);

      setLegalDocuments({
        terms: termsResult.data?.data,
        privacy: privacyResult.data?.data,
      });
      setLegalDocumentsLoading(false);
    })();
  }, []);

  const legalAcceptances = useMemo<TLegalAcceptanceInput[]>(() => {
    const acceptances: TLegalAcceptanceInput[] = [];
    if (legalDocuments.terms) {
      acceptances.push({
        document_type: legalDocuments.terms.document_type,
        version: legalDocuments.terms.version,
      });
    }
    if (legalDocuments.privacy) {
      acceptances.push({
        document_type: legalDocuments.privacy.document_type,
        version: legalDocuments.privacy.version,
      });
    }
    return acceptances;
  }, [legalDocuments]);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      form
        .handleSubmit(async (payload) => {
          if (legalAcceptances.length < 2) {
            toast({
              variant: "destructive",
              title: "Legal documents unavailable",
              description: "Terms and Privacy Policy must be available before you can register.",
            });
            return;
          }

          const { data, error } = await authService.signup(payload, legalAcceptances, undefined, inviteToken);

          if (data) {
            // Silent auto-login with the credentials the user just typed —
            // signup momentum shouldn't die behind a second login form and a
            // detached "check your email, come back, log in" step.
            const { data: loginData } = await authService.login({
              email: payload.email,
              password: payload.password,
            });

            if (loginData && !loginData.data.requires_2fa) {
              updateAuthStore({
                accessToken: loginData.data.access_token,
                expiresAt: getJwtExpiration(loginData.data.access_token) ?? undefined,
              });

              const { data: profileData } = await userService.getProfile();
              if (profileData) {
                updateAuthStore({ profile: profileData });
              }

              // Invited users arrive pre-verified server-side — skip the OTP step entirely.
              if (profileData?.email_verified) {
                toast({ variant: "success", title: "Account created!", description: "Welcome to Fundy." });
                await router
                  .invalidate()
                  .finally(() => {
                    window.location.assign("/app");
                  })
                  .catch(console.error);
                return;
              }

              toast({
                variant: "success",
                title: "Account created!",
                description: "We've sent a verification code to your email — it should be there already.",
              });
              void navigate({
                to: "/auth/verify-otp",
                search: { mode: "email", email: payload.email, phone: undefined, from: undefined },
              });
              return;
            }

            // Auto-login unavailable (rare) — fall back to the login page as before.
            toast({
              variant: "success",
              title: "Account created!",
              description: "Log in to continue.",
            });
            // Hand the email to the login form (session-scoped, not via URL).
            try {
              sessionStorage.setItem("fundy:lastSignupEmail", payload.email);
            } catch {
              // Storage unavailable (private mode) — user just types their email.
            }
            void navigate({ to: "/auth/login", search: { from: undefined } });
          }

          if (error) {
            await handleFormError(error, form);
          }
        })(e)
        .catch(console.error);
    },
    [form, legalAcceptances, toast, navigate, router, updateAuthStore, inviteToken]
  );

  return {
    form,
    handleSubmit,
    legalDocuments,
    legalDocumentsLoading,
  };
};
